import { useEffect, useState } from 'react'
import { Button, message, Spin, Modal } from 'antd'

import ControleAcessoFilter from 'Components/ControleAcesso/Filter'
import ControleAcessoTree from 'Components/ControleAcesso/Tree'

import EAFloatingActionBar from 'Components/UI/EAFloatingActionBar'
import EARequestErrorResult from 'Components/UI/EARequestErrorResult'

import useControleAcesso from 'Hooks/controleAcesso/useControleAcesso'
import useOperacoes from 'Hooks/operacao/useOperacoes'

import controleAcessoApiService from 'Services/api/controleAcesso'

import { DependenciasOperacoes } from 'Components/Acesso/ControleAcessoOperacoes'

// Função utilitária para buscar a descrição de uma operação pelo id (busca recursiva)
function getDescricaoOperacaoById(operacoes, id) {
  // Função auxiliar para buscar recursivamente
  function buscarRecursivamente(items) {
    const encontrado = items.find(
      (item) => String(item.idoperacao) === String(id)
    )
    if (encontrado) {
      return encontrado.descricao
    }

    return items.reduce((result, item) => {
      if (result) return result
      if (item.suboperacoes && item.suboperacoes.length > 0) {
        return buscarRecursivamente(item.suboperacoes)
      }
      return result
    }, null)
  }

  const descricao = buscarRecursivamente(operacoes)
  return descricao || `Operação ${id}` // fallback para o id caso não encontre
}

const ControleAcessoPage = () => {
  const { controleAcessoRequest, fetchControleAcesso } = useControleAcesso()
  const { operacoesRequest } = useOperacoes()
  const [
    hasSubmittedControleAcessoFilter,
    setHasSubmittedControleAcessoFilter
  ] = useState(false)
  const [idEstabelecimentoSelecionado, setIdEstabelecimentoSelecionado] =
    useState(null)
  const [idUsuarioSelecionado, setIdUsuarioSelecionado] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [operacoesUsuario, setOperacoesUsuario] = useState([])
  const [modalDependencias, setModalDependencias] = useState({
    visible: false,
    dependencias: [],
    tipo: null, // 'adicionar' ou 'remover'
    operacao: null,
    novoOperacoesUsuario: []
  })

  useEffect(() => {
    setOperacoesUsuario(
      controleAcessoRequest.data.map((operacao) => operacao.idoperacao)
    )
  }, [controleAcessoRequest.data])

  const filtrarOperacoesUsuario = (filtros) => {
    fetchControleAcesso(filtros).then(() => {
      setHasSubmittedControleAcessoFilter(true)
    })
  }

  const atualizarOperacoesUsuario = () => {
    const idestabelecimento = idEstabelecimentoSelecionado
    const idusuario = idUsuarioSelecionado
    const operacoes = operacoesUsuario.map((idoperacao) => ({
      idoperacao
    }))

    setIsLoading(true)

    controleAcessoApiService
      .updateBy({ idestabelecimento, idusuario }, operacoes)
      .then(() => {
        message.success(
          'As permissões do usuário foram atualizadas com sucesso.'
        )
      })
      .catch(() => {
        message.error('Erro ao tentar atualizar as permissões do usuário.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const resetarPermissoesUsuario = () => {
    if (hasSubmittedControleAcessoFilter) {
      message.info(
        'É necessário filtrar novamente as permissões do usuário após a alteração dos filtros.'
      )
    }
    setHasSubmittedControleAcessoFilter(false)
    setOperacoesUsuario([])
  }

  const alterarEstabelecimentoEResetarPermissoesUsuario = (
    idestabelecimento
  ) => {
    setIdEstabelecimentoSelecionado(idestabelecimento)
    resetarPermissoesUsuario()
  }

  const alterarUsuarioEResetarPermissoesUsuario = (idusuario) => {
    setIdUsuarioSelecionado(idusuario)
    resetarPermissoesUsuario()
  }

  // Função intermediária para tratar dependências
  const handleChangeOperacoesUsuario = (checkedKeys, e) => {
    const { node, checked } = e
    const idoperacao = String(node.key)
    const dependencias = DependenciasOperacoes[idoperacao] || []
    const novasOperacoes = checkedKeys.map(String)

    // Encontrar todas as permissões que dependem da que está sendo alterada
    const dependentes = Object.entries(DependenciasOperacoes)
      .filter(([, deps]) => deps.includes(idoperacao))
      .map(([op]) => String(op))

    if (checked && dependencias.length > 0) {
      // Adicionando permissão com dependências
      const faltando = dependencias.filter(
        (dep) => !novasOperacoes.includes(dep)
      )
      if (faltando.length > 0) {
        setModalDependencias({
          visible: true,
          dependencias: faltando,
          tipo: 'adicionar',
          operacao: idoperacao,
          novoOperacoesUsuario: [...novasOperacoes, ...faltando]
        })
        return
      }
    } else if (!checked) {
      // Removendo permissão, verificar se precisa remover dependências
      // Verificar quais permissões dependentes ainda estão selecionadas
      const aindaSelecionados = dependentes.filter((dep) =>
        novasOperacoes.includes(dep)
      )

      // Se houver permissões dependentes ainda selecionadas, mostrar o modal
      if (aindaSelecionados.length > 0) {
        setModalDependencias({
          visible: true,
          dependencias: aindaSelecionados,
          tipo: 'remover',
          operacao: idoperacao,
          novoOperacoesUsuario: novasOperacoes.filter(
            (op) => !aindaSelecionados.includes(op)
          )
        })
        return
      }
    }
    setOperacoesUsuario(novasOperacoes)
  }

  // Função para confirmar ação do modal
  const handleConfirmDependencias = () => {
    setOperacoesUsuario(modalDependencias.novoOperacoesUsuario)
    setModalDependencias({ ...modalDependencias, visible: false })
  }

  // Função para confirmar apenas a permissão selecionada
  const handleConfirmApenasEsta = () => {
    const { operacao, tipo } = modalDependencias
    const novasOperacoes = [...operacoesUsuario]

    if (tipo === 'adicionar') {
      // Adiciona apenas a permissão selecionada
      if (!novasOperacoes.includes(operacao)) {
        novasOperacoes.push(operacao)
      }
    } else {
      // Remove apenas a permissão selecionada
      const index = novasOperacoes.indexOf(operacao)
      if (index > -1) {
        novasOperacoes.splice(index, 1)
      }
    }

    setOperacoesUsuario(novasOperacoes)
    setModalDependencias({ ...modalDependencias, visible: false })
  }

  // Função para cancelar ação do modal
  const handleCancelDependencias = () => {
    setModalDependencias({ ...modalDependencias, visible: false })
  }

  if (controleAcessoRequest.hasError || operacoesRequest.hasError) {
    return <EARequestErrorResult />
  }

  return (
    <Spin
      spinning={
        isLoading ||
        controleAcessoRequest.isLoading ||
        operacoesRequest.isLoading
      }
    >
      <ControleAcessoFilter
        onChangeEstabelecimento={
          alterarEstabelecimentoEResetarPermissoesUsuario
        }
        onChangeUsuario={alterarUsuarioEResetarPermissoesUsuario}
        onSubmit={filtrarOperacoesUsuario}
      />

      {hasSubmittedControleAcessoFilter && (
        <>
          <ControleAcessoTree
            operacoes={operacoesRequest.data}
            operacoesUsuario={operacoesUsuario}
            onChange={handleChangeOperacoesUsuario}
          />
          <Modal
            visible={modalDependencias.visible}
            onOk={handleConfirmDependencias}
            onCancel={handleCancelDependencias}
            title={
              modalDependencias.tipo === 'adicionar'
                ? 'Dependências necessárias'
                : 'Remover dependentes'
            }
            okText={
              modalDependencias.tipo === 'adicionar'
                ? 'Adicionar todas'
                : 'Remover todos'
            }
            cancelText="Cancelar"
            footer={[
              <Button key="cancel" onClick={handleCancelDependencias}>
                Cancelar
              </Button>,
              <Button key="apenas" onClick={handleConfirmApenasEsta}>
                Apenas esta
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleConfirmDependencias}
              >
                {modalDependencias.tipo === 'adicionar'
                  ? 'Adicionar todas'
                  : 'Remover todos'}
              </Button>
            ]}
          >
            {modalDependencias.tipo === 'adicionar' ? (
              <span>
                Esta permissão tem as seguintes dependências:
                <ul style={{ margin: '8px 0 0 16px' }}>
                  {modalDependencias.dependencias.map((depId) => (
                    <li key={depId}>
                      {getDescricaoOperacaoById(operacoesRequest.data, depId)}
                    </li>
                  ))}
                </ul>
                Deseja adicionar todas?
              </span>
            ) : (
              <span>
                Outras permissões dependem desta:
                <ul style={{ margin: '8px 0 0 16px' }}>
                  {modalDependencias.dependencias.map((depId) => (
                    <li key={depId}>
                      {getDescricaoOperacaoById(operacoesRequest.data, depId)}
                    </li>
                  ))}
                </ul>
                Deseja remover todas?
              </span>
            )}
          </Modal>
        </>
      )}

      {operacoesUsuario.length > 0 && (
        <EAFloatingActionBar>
          <Button type="primary" onClick={atualizarOperacoesUsuario}>
            Atualizar
          </Button>
        </EAFloatingActionBar>
      )}
    </Spin>
  )
}

export default ControleAcessoPage
